import React, { useEffect, useState } from 'react';
import { SavedIcon, Spinner } from 'evergreen-ui';
import AppButton from 'components/AppButton/AppButton';
import { getApiErrorMessage } from 'utils/apiError';
import { GroupRelationsType } from '../../../../store/api/groups';
import {
    useGetRecruiteeIntegrationQuery,
    useSetGroupRecruiteeMutation,
} from '../../../../store/api/integrations';
import { TabPanel } from '../components/TabPanel';
import { SwitchRow } from '../components/SwitchRow';
import styles from '../TargetGroupSettings.module.css';

interface RecruiteeTabProps {
    tab: string;
    index: number;
    selectedIndex: number;
    companySlug: string;
    groupId: string;
    relationsType?: GroupRelationsType;

    isRecruiteeActive: boolean;
    setIsRecruiteeActive: (value: boolean) => void;
}

/**
 * Recruitee tab — makes the group available in Tellent Recruitee. Only shown
 * for CANDIDATE groups; multi-step (base or linked) groups can't be enabled.
 *
 * The switch value is owned by TargetGroupSettings (hydrated in
 * setFieldsFromState, so closing the popover resets it), but saving goes
 * through its own endpoint, so this tab owns its own mutation, loading,
 * error and "Saved!" state instead of the shared updateGroupConfig flow.
 */
export const RecruiteeTab = ({
    tab,
    index,
    selectedIndex,
    companySlug,
    groupId,
    relationsType,
    isRecruiteeActive,
    setIsRecruiteeActive,
}: RecruiteeTabProps) => {
    const { data: recruiteeIntegration } =
        useGetRecruiteeIntegrationQuery(companySlug);
    const [setGroupRecruitee, { isLoading, error }] =
        useSetGroupRecruiteeMutation();
    const [displaySuccess, setDisplaySuccess] = useState<boolean>(false);

    const isMultiStepGroup =
        relationsType === 'base' || relationsType === 'linked';
    // The toggle works without a key, so admins can prepare groups before
    // connecting; we only point out that nothing is connected yet.
    const isNotConnected =
        !!recruiteeIntegration && !recruiteeIntegration.connected;

    const onSave = () => {
        setDisplaySuccess(false);
        setGroupRecruitee({
            companyIdentifier: companySlug,
            groupId,
            isActive: isRecruiteeActive,
        })
            .unwrap()
            .then(() => setDisplaySuccess(true))
            // The error is rendered from the mutation state.
            .catch(() => undefined);
    };

    useEffect(() => {
        if (!displaySuccess) return;
        const timeoutId = setTimeout(() => setDisplaySuccess(false), 5000);
        return () => clearTimeout(timeoutId);
    }, [displaySuccess]);

    return (
        <TabPanel tab={tab} index={index} selectedIndex={selectedIndex}>
            <table>
                <colgroup>
                    <col className={styles.setting_key} />
                    <col className={styles.setting_value} />
                </colgroup>
                <tbody>
                    <SwitchRow
                        label="Available in Tellent Recruitee"
                        description={
                            <>
                                <p>
                                    Recruiters can send this scan to candidates
                                    from Tellent Recruitee.
                                </p>
                                {isMultiStepGroup && (
                                    <p>
                                        <span
                                            className={
                                                styles.target_group_settings__pane__configuration__bold_teal_text
                                            }>
                                            Multi-step scans can't be sent from
                                            Recruitee.
                                        </span>
                                    </p>
                                )}
                                {isNotConnected && (
                                    <p>
                                        <span
                                            className={
                                                styles.target_group_settings__pane__configuration__bold_teal_text
                                            }>
                                            Your company hasn't connected
                                            Recruitee yet. A company admin can
                                            do this under Integrations.
                                        </span>
                                    </p>
                                )}
                            </>
                        }
                        checked={!isMultiStepGroup && isRecruiteeActive}
                        onChange={setIsRecruiteeActive}
                        disabled={isMultiStepGroup}
                    />
                </tbody>
            </table>
            <div className={styles.submit_wrap}>
                <span
                    className={
                        error && !isLoading
                            ? styles.save_error
                            : styles.save_result
                    }>
                    {isLoading ? (
                        <Spinner size={15} />
                    ) : error ? (
                        getApiErrorMessage(error)
                    ) : displaySuccess ? (
                        'Saved!'
                    ) : (
                        ''
                    )}
                </span>
                <AppButton
                    icon={SavedIcon}
                    onClick={onSave}
                    width={'120px'}
                    isDisabled={isLoading || isMultiStepGroup}>
                    Save
                </AppButton>
            </div>
        </TabPanel>
    );
};

export default RecruiteeTab;
